import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Grid2 as Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import {
  Add,
  Search,
  Visibility,
  Edit,
  Assignment,
  Send,
  Refresh,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  loadSubmissions,
  assignReviewer,
  sendReminderAction,
  setSubmissionFilters,
} from "../../../../store/actions/ppActions";

const PpSubmissionsListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const ppState = useSelector((state) => state[currentSession]?.pp);
  const submissions = ppState?.submissions?.data || [];
  const loading = ppState?.submissions?.loading || false;
  const filters = ppState?.submissions?.filters || {};

  const [searchTerm, setSearchTerm] = useState(filters.search || "");
  const [statusFilter, setStatusFilter] = useState(filters.status || "");
  const [dueBucketFilter, setDueBucketFilter] = useState(
    filters.dueBucket || ""
  );
  const [contractorFilter, setContractorFilter] = useState(
    filters.contractor || ""
  );

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedReviewer, setSelectedReviewer] = useState("");

  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);

  // Mock reviewers list
  const reviewers = [
    "John Smith, PE",
    "Jane Doe, PE",
    "Mike Johnson, AIA",
    "Sarah Wilson, PE",
  ];

  useEffect(() => {
    const currentFilters = {
      search: searchTerm,
      status: statusFilter,
      dueBucket: dueBucketFilter,
      contractor: contractorFilter,
    };
    dispatch(loadSubmissions(currentFilters));
    dispatch(setSubmissionFilters(currentFilters));
  }, [dispatch, searchTerm, statusFilter, dueBucketFilter, contractorFilter]);

  const handleSearch = () => {
    const currentFilters = {
      search: searchTerm,
      status: statusFilter,
      dueBucket: dueBucketFilter,
      contractor: contractorFilter,
    };
    dispatch(loadSubmissions(currentFilters));
  };

  const handleAssignReviewer = async () => {
    if (!selectedSubmission || !selectedReviewer) return;

    try {
      await dispatch(assignReviewer(selectedSubmission.id, selectedReviewer));
      setAssignDialogOpen(false);
      setSelectedSubmission(null);
      setSelectedReviewer("");
    } catch (error) {
      console.error("Failed to assign reviewer:", error);
    }
  };

  const handleSendReminder = async () => {
    if (!selectedSubmission) return;

    try {
      setSendingReminder(true);
      await dispatch(sendReminderAction(selectedSubmission.id));
      setReminderDialogOpen(false);
      setSelectedSubmission(null);
    } catch (error) {
      console.error("Failed to send reminder:", error);
    } finally {
      setSendingReminder(false);
    }
  };

  const getDueBadgeColor = (bucket) => {
    switch (bucket) {
      case "75%":
        return "success";
      case "50%":
        return "warning";
      case "25%":
        return "error";
      case "past_due":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: "default", label: "Pending" },
      in_review: { color: "info", label: "In Review" },
      corrections_needed: { color: "warning", label: "Corrections Needed" },
      approved: { color: "success", label: "Approved" },
      submitted_to_city: { color: "primary", label: "Submitted to City" },
    };

    const config = statusConfig[status] || { color: "default", label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography color="primary" variant="h4">
          Plan Review Submissions
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate("/pp/submissions/new")}
        >
          New Submission
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Search"
                placeholder="Permit, address, contractor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                slotProps={{
                  input: {
                    endAdornment: (
                      <IconButton onClick={handleSearch}>
                        <Search />
                      </IconButton>
                    ),
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_review">In Review</MenuItem>
                  <MenuItem value="corrections_needed">
                    Corrections Needed
                  </MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Due Status</InputLabel>
                <Select
                  value={dueBucketFilter}
                  label="Due Status"
                  onChange={(e) => setDueBucketFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="75%">75% (3+ days)</MenuItem>
                  <MenuItem value="50%">50% (2-3 days)</MenuItem>
                  <MenuItem value="25%">25% (1 day)</MenuItem>
                  <MenuItem value="past_due">Past Due</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Contractor"
                placeholder="Filter by contractor..."
                value={contractorFilter}
                onChange={(e) => setContractorFilter(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => dispatch(loadSubmissions())}
                disabled={loading}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Submissions Table */}
      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Permit/Address</TableCell>
                <TableCell>Contractor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Due</TableCell>
                <TableCell>Reviewer</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Loading submissions...
                  </TableCell>
                </TableRow>
              ) : submissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No submissions found
                  </TableCell>
                </TableRow>
              ) : (
                submissions.map((submission) => {
                  const daysUntilDue = getDaysUntilDue(submission.dueDate);
                  return (
                    <TableRow key={submission.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {submission.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {submission.permitNumber || "No Permit #"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {submission.address}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {submission.contractor.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {submission.contractor.license}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{getStatusChip(submission.status)}</TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Chip
                            label={submission.dueBucket}
                            color={getDueBadgeColor(submission.dueBucket)}
                            size="small"
                          />
                          <Typography variant="caption" color="text.secondary">
                            {daysUntilDue !== null &&
                              (daysUntilDue < 0
                                ? `${Math.abs(daysUntilDue)} days overdue`
                                : daysUntilDue === 0
                                  ? "Due today"
                                  : `${daysUntilDue} days left`)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {submission.assignedReviewer ? (
                          <Typography variant="body2">
                            {submission.assignedReviewer}
                          </Typography>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Assignment />}
                            onClick={() => {
                              setSelectedSubmission(submission);
                              setAssignDialogOpen(true);
                            }}
                          >
                            Assign
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() =>
                                navigate(`/pp/submissions/${submission.id}`)
                              }
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          {submission.workbench && (
                            <Tooltip title="Review Workbench">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  navigate(`/pp/workbench/${submission.id}`)
                                }
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                          )}
                          {submission.assignedReviewer && (
                            <Tooltip title="Send Reminder">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => {
                                  setSelectedSubmission(submission);
                                  setReminderDialogOpen(true);
                                }}
                              >
                                <Send />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Assign Reviewer Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
      >
        <DialogTitle>Assign Reviewer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Assign a reviewer to submission: {selectedSubmission?.id}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Reviewer</InputLabel>
            <Select
              value={selectedReviewer}
              label="Reviewer"
              onChange={(e) => setSelectedReviewer(e.target.value)}
            >
              {reviewers.map((reviewer) => (
                <MenuItem key={reviewer} value={reviewer}>
                  {reviewer}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignReviewer}
            disabled={!selectedReviewer}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Reminder Dialog */}
      <Dialog
        open={reminderDialogOpen}
        onClose={() => setReminderDialogOpen(false)}
      >
        <DialogTitle>Send Reminder</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Send a reminder notification for submission:{" "}
            {selectedSubmission?.id}?
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            This will notify the assigned reviewer that the submission is due
            soon.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReminderDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSendReminder}
            disabled={sendingReminder}
          >
            {sendingReminder ? "Sending..." : "Send Reminder"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PpSubmissionsListPage;
